import axios from 'axios';
import config from '../config';
import applyTokenRefreshInterceptor from '../utils/tokenRefreshInterceptor';
import applyAuthRetryInterceptor from '../utils/applyAuthRetryInterceptor';

const getToken = () => localStorage.getItem('token');

const fprApiClient = axios.create({
  baseURL: config.api.clientFormsServiceUrl || config.api.fprServiceUrl,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

// All FPR admin routes are namespaced under /admin/fpr on the unified
// Client Forms service.
const ADMIN = '/admin/fpr';

fprApiClient.interceptors.request.use(
  (cfg) => {
    const token = getToken();
    if (token) {
      cfg.headers['Authorization'] = `Bearer ${token}`;
    }
    return cfg;
  },
  (error) => Promise.reject(error),
);

applyAuthRetryInterceptor(fprApiClient);
applyTokenRefreshInterceptor(fprApiClient);

const fprService = {
  fetchAllClients: async () => {
    const response = await fprApiClient.get(`${ADMIN}/clients`);
    return response.data;
  },

  createClient: async (clientData) => {
    const response = await fprApiClient.post(`${ADMIN}/clients`, clientData);
    return response.data;
  },

  deleteClient: async (clientId) => {
    const response = await fprApiClient.delete(`${ADMIN}/clients/${clientId}`);
    return response.data;
  },

  resendLink: async (clientId) => {
    const response = await fprApiClient.post(`${ADMIN}/clients/${clientId}/resend`);
    return response.data;
  },

  getLink: async (clientId) => {
    const response = await fprApiClient.post(
      `${ADMIN}/clients/${clientId}/resend?send_notification=false`,
    );
    return response.data;
  },

  getClientDetail: async (clientId, options = {}) => {
    const response = await fprApiClient.get(`${ADMIN}/clients/${clientId}`, {
      signal: options.signal,
    });
    return response.data;
  },

  patchClient: async (clientId, fields) => {
    const response = await fprApiClient.patch(`${ADMIN}/clients/${clientId}`, fields);
    return response.data;
  },

  scheduleSend: async (clientId, payload) => {
    const response = await fprApiClient.post(
      `${ADMIN}/clients/${clientId}/schedule-send`,
      payload,
    );
    return response.data;
  },

  cancelScheduledSend: async (clientId) => {
    const response = await fprApiClient.delete(
      `${ADMIN}/clients/${clientId}/schedule-send`,
    );
    return response.data;
  },

  searchTamaracClients: async (query, limit = 25) => {
    const response = await fprApiClient.get(`${ADMIN}/tamarac/clients`, {
      params: { query, limit },
    });
    return response.data?.clients || [];
  },

  searchTamaracClientsPA: async (query, maxResults = 25) => {
    const response = await fprApiClient.get(`${ADMIN}/tamarac/clients/search-pa`, {
      params: { query, limit: maxResults },
      timeout: 45000,
    });
    return response.data?.clients || [];
  },

  fetchAllTamaracClients: async (maxClients = 50000) => {
    const response = await fprApiClient.get(`${ADMIN}/tamarac/clients/all`, {
      params: { max_clients: maxClients },
      timeout: 300000,
    });
    return response.data?.clients || [];
  },

  fetchTamaracContactDetails: async (contactId, options = {}) => {
    const response = await fprApiClient.get(
      `${ADMIN}/tamarac/contacts/${encodeURIComponent(contactId)}/details`,
      { signal: options.signal },
    );
    return response.data || {};
  },
};

export default fprService;
